// src/ui/GroupSettings.jsx — group info, permissions, requests and admins.
//
// Everything here writes the app's own fields and posts the app's own system
// messages, so a change made in this browser lands identically on every phone:
// the row appears in the transcript, the chat-list preview updates, badges
// increment, and the socket relay makes the server push it.
//
// Who may do what mirrors the phone exactly:
//   • name / description / photo → admins, or everyone when `editSettings`
//   • the permission switches, approvals and admin management → admins only

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Avatar from './Avatar';
import { Check, X, Image as ImageIcon, Trash, Group as GroupIcon, PersonAdd2 } from './icons';
import { getUserProfile } from './../services/users';
import { tsMs } from '../services/chats';
import {
  permOf, isGroupAdmin, activeMembers, canEditInfo,
  setPermission, disableApprovalAndAdmitAll, setGroupName, setGroupDescription,
  setGroupImage, removeGroupImage, toggleAdmin, approveRequest, declineRequest,
  tagOf, canTagMember,
} from '../services/groupAdmin';
import MemberTagEditor from './MemberTagEditor';

function Toggle({ on, onFlip, label, sub, disabled }) {
  return (
    <div className={`ap-toggle-row ${disabled ? 'ci-dimmed' : ''}`}>
      <div>{label}{sub ? <i>{sub}</i> : null}</div>
      <button
        className={`ap-switch ${on ? 'on' : ''}`} onClick={disabled ? undefined : onFlip}
        role="switch" aria-checked={on} aria-label={label}
      />
    </div>
  );
}

/** One member row — name, admin pill, and (for admins) promote / dismiss. */
function MemberRow({ chat, uid, myUid, amAdmin, onToggleAdmin, busy, onEditTag }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    let live = true;
    getUserProfile(uid).then((x) => live && setP(x)).catch(() => {});
    return () => { live = false; };
  }, [uid]);
  const isAdm = isGroupAdmin(chat, uid);
  const name = uid === myUid ? 'You'
    : (p?.fullName || p?.username || chat.participantNames?.[uid] || 'Member');
  const tag = tagOf(chat, uid);
  /* the phone's rule: yourself always, anyone else only as an admin */
  const mayTag = canTagMember(chat, myUid, uid);
  return (
    <div className="grp-member">
      <Avatar profile={p} size="sm" myUid={myUid} />
      <span className="grp-mname">
        {name}
        {/* 🏷 the tag sits with the name, the way it does on every bubble */}
        {tag && <em className="grp-mtag">{tag}</em>}
        {isAdm && <em className="grp-admin">Admin</em>}
      </span>
      {mayTag && (
        <button
          className="grp-tagbtn"
          disabled={busy}
          onClick={() => onEditTag(uid, name, p, tag)}
          title={tag ? `Edit ${name}'s tag` : `Add a tag for ${name}`}
        >{tag ? 'Edit tag' : 'Add tag'}</button>
      )}
      {amAdmin && uid !== myUid && (
        <button className="ci-chip" disabled={busy} onClick={() => onToggleAdmin(uid)}>
          {isAdm ? 'Dismiss' : 'Make admin'}
        </button>
      )}
    </div>
  );
}

/**
 * Who made this group, and when.
 *
 * 🟥 THE NAME HAS THREE SOURCES, IN THIS ORDER, AND THE ORDER MATTERS. The phone
 *    reads it off `groupMembers.find(m => m.isCreator)`, which is built from the
 *    CURRENT participant list — so once the founder leaves the group, the phone
 *    shows "Unknown". Here the live user doc is tried first (a renamed founder
 *    shows their new name), then `participantNames`, which is a frozen snapshot
 *    taken at creation and therefore survives them leaving. Display only: nothing
 *    is written, so this can be kinder than the phone without desyncing anything.
 *
 * `createdBy` and `createdAt` are written by BOTH group creators — the app's
 * NewGroupModal and this web's services/groupCreate.js — so any group made since
 * either shipped has them. Groups older than the fields simply don't render.
 */
function CreatedBy({ chat, myUid }) {
  const uid = chat.createdBy || '';
  const [p, setP] = useState(null);
  useEffect(() => {
    let live = true;
    if (uid && uid !== myUid) getUserProfile(uid).then((x) => live && setP(x)).catch(() => {});
    return () => { live = false; };
  }, [uid, myUid]);

  if (!uid && !chat.createdAt) return null;

  const name = !uid ? 'Unknown'
    : uid === myUid ? 'You'
      : (p?.fullName || p?.username || chat.participantNames?.[uid] || 'Unknown');

  // the phone's exact format string, so the two screens read the same
  const ms = tsMs(chat.createdAt);
  const when = ms
    ? new Date(ms).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
    : '';

  return (
    <div className="grp-field">
      <span className="ci-picklabel">Created by</span>
      <div className="grp-created">
        {uid && uid !== myUid && <Avatar profile={p} size="sm" myUid={myUid} />}
        <span>
          <b>{name}</b>
          {when && <i>{when}</i>}
        </span>
      </div>
    </div>
  );
}

export default function GroupSettings({ chat, myUid, myName }) {
  const me = useMemo(() => ({ uid: myUid, name: myName || 'A member' }), [myUid, myName]);
  const amAdmin = isGroupAdmin(chat, myUid);
  const mayEdit = canEditInfo(chat, myUid);
  const members = activeMembers(chat);
  const pending = chat.pendingParticipants || [];

  const [name, setName] = useState(chat.name || '');
  const [desc, setDesc] = useState(chat.description || '');
  const [editing, setEditing] = useState(null);      // 'name' | 'desc' | null
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [askApproveOff, setAskApproveOff] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [tagging, setTagging] = useState(null);   // { uid, name, profile, tag }
  const picRef = useRef(null);

  useEffect(() => { setName(chat.name || ''); setDesc(chat.description || ''); setEditing(null); }, [chat.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (tag, fn) => {
    setBusy(tag); setErr('');
    try { await fn(); } catch (e) { setErr(e?.userMessage || 'That change didn’t go through — try again.'); }
    setBusy('');
  };

  const flip = (key, value) => {
    // turning approval OFF with people waiting admits them, exactly like the phone
    if (key === 'approveMembers' && value === false && pending.length > 0) { setAskApproveOff(true); return; }
    run(key, () => setPermission(chat, me, key, value));
  };

  return (
    <div className="ci-sec ci-group">
      <div className="ci-sec-title">
        <GroupIcon width={15} height={15} /> Group settings
        <em>{amAdmin ? 'you are an admin' : 'synced'}</em>
      </div>

      {/* ── identity: photo, name, description ── */}
      <div className="grp-identity">
        <button
          className={`grp-photo ${mayEdit ? '' : 'locked'}`}
          onClick={() => mayEdit && picRef.current?.click()}
          title={mayEdit ? 'Change group photo' : 'Only admins can change the group photo'}
        >
          <Avatar chat={chat} myUid={myUid} />
          {mayEdit && <span className="grp-photo-edit"><ImageIcon width={13} height={13} /></span>}
        </button>
        <input
          ref={picRef} type="file" accept="image/*" hidden
          onChange={(e) => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (f) run('photo', () => setGroupImage(chat, me, f));
          }}
        />
        {mayEdit && chat.groupImage && (
          <button className="ci-chip" disabled={busy === 'photo'} onClick={() => run('photo', () => removeGroupImage(chat, me))}>
            <Trash width={12} height={12} /> Remove photo
          </button>
        )}
        {busy === 'photo' && <span className="grp-busy">Updating photo…</span>}
      </div>

      <div className="grp-field">
        <span className="ci-picklabel">Group name</span>
        {editing === 'name' ? (
          <div className="grp-edit">
            <input value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} />
            <button className="ci-chip on" disabled={busy === 'name'} onClick={() => run('name', async () => { await setGroupName(chat, me, name); setEditing(null); })}>
              <Check width={12} height={12} /> Save
            </button>
            <button className="ci-chip" onClick={() => { setName(chat.name || ''); setEditing(null); }}>Cancel</button>
          </div>
        ) : (
          <button className={`grp-value ${mayEdit ? '' : 'locked'}`} onClick={() => mayEdit && setEditing('name')}>
            {chat.name || 'Unnamed group'}
          </button>
        )}
      </div>

      <CreatedBy chat={chat} myUid={myUid} />

      <div className="grp-field">
        <span className="ci-picklabel">Description</span>
        {editing === 'desc' ? (
          <div className="grp-edit col">
            <textarea value={desc} rows={3} maxLength={500} autoFocus onChange={(e) => setDesc(e.target.value)} />
            <div>
              <button className="ci-chip on" disabled={busy === 'desc'} onClick={() => run('desc', async () => { await setGroupDescription(chat, me, desc); setEditing(null); })}>
                <Check width={12} height={12} /> Save
              </button>
              <button className="ci-chip" onClick={() => { setDesc(chat.description || ''); setEditing(null); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className={`grp-value ${mayEdit ? '' : 'locked'}`} onClick={() => mayEdit && setEditing('desc')}>
            {chat.description || (mayEdit ? 'Add a description' : 'No description')}
          </button>
        )}
      </div>

      {!mayEdit && <div className="ci-note">Only admins can edit this group’s name, description and photo.</div>}

      {/* ── permissions: admins only ── */}
      <div className="grp-block">
        <span className="ci-picklabel">Members can</span>
        <Toggle
          label="Send messages" sub={permOf(chat, 'sendMessages') ? 'Everyone can write here' : 'Only admins can send messages'}
          on={permOf(chat, 'sendMessages')} disabled={!amAdmin}
          onFlip={() => flip('sendMessages', !permOf(chat, 'sendMessages'))}
        />
        <Toggle
          label="Add other members" sub={permOf(chat, 'addMembers') ? 'Any member can add people' : 'Only admins can add people'}
          on={permOf(chat, 'addMembers')} disabled={!amAdmin}
          onFlip={() => flip('addMembers', !permOf(chat, 'addMembers'))}
        />
        <Toggle
          label="Edit group info" sub={permOf(chat, 'editSettings') ? 'Any member can change name, photo and description' : 'Only admins can change group info'}
          on={permOf(chat, 'editSettings')} disabled={!amAdmin}
          onFlip={() => flip('editSettings', !permOf(chat, 'editSettings'))}
        />
        <Toggle
          label="Start calls" sub={permOf(chat, 'makeCalls') ? 'Any member can start a group call' : 'Only admins can start calls'}
          on={permOf(chat, 'makeCalls')} disabled={!amAdmin}
          onFlip={() => flip('makeCalls', !permOf(chat, 'makeCalls'))}
        />
      </div>

      <div className="grp-block">
        <span className="ci-picklabel">Admin approval</span>
        <Toggle
          label="Approve new members"
          sub={permOf(chat, 'approveMembers')
            ? 'People added by members wait for an admin'
            : 'Anyone added joins straight away'}
          on={permOf(chat, 'approveMembers')} disabled={!amAdmin}
          onFlip={() => flip('approveMembers', !permOf(chat, 'approveMembers'))}
        />
        {askApproveOff && (
          <div className="ci-verify-ask">
            <span>{pending.length} {pending.length === 1 ? 'person is' : 'people are'} waiting. Turning approval off admits {pending.length === 1 ? 'them' : 'them all'} now.</span>
            <div>
              <button className="ci-chip on" onClick={() => { setAskApproveOff(false); run('approveMembers', () => disableApprovalAndAdmitAll(chat, me)); }}>
                Admit &amp; turn off
              </button>
              <button className="ci-chip" onClick={() => setAskApproveOff(false)}>Cancel</button>
            </div>
          </div>
        )}
        {!amAdmin && <div className="ci-note">Only admins can change these.</div>}
      </div>

      {/* ── waiting for approval ── */}
      {amAdmin && pending.length > 0 && (
        <div className="grp-block">
          <span className="ci-picklabel">Waiting for approval · {pending.length}</span>
          {pending.map((uid) => (
            <div className="grp-member" key={uid}>
              <Avatar chat={chat} peerUid={uid} size="sm" myUid={myUid} />
              <span className="grp-mname">{chat.participantNames?.[uid] || 'A new member'}</span>
              <button className="ci-chip on" disabled={busy === uid} onClick={() => run(uid, () => approveRequest(chat, me, uid))}>
                <Check width={12} height={12} /> Approve
              </button>
              <button className="ci-chip" disabled={busy === uid} onClick={() => run(uid, () => declineRequest(chat, uid))}>
                <X width={12} height={12} /> Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── admins + members ── */}
      <div className="grp-block">
        <span className="ci-picklabel">
          Group admins · {(chat.admins || []).length}
        </span>
        {(chat.admins || []).filter((u) => members.includes(u)).map((uid) => (
          <MemberRow key={uid} chat={chat} uid={uid} myUid={myUid} amAdmin={amAdmin} busy={busy === uid}
            onToggleAdmin={(u) => run(u, () => toggleAdmin(chat, me, u))}
            onEditTag={(u, n, prof, t) => setTagging({ uid: u, name: n, profile: prof, tag: t })} />
        ))}
        {(chat.admins || []).length === 0 && <div className="ci-empty">No admins yet</div>}

        <button className="ci-chip grp-more" onClick={() => setShowMembers(!showMembers)}>
          <PersonAdd2 width={12} height={12} />
          {showMembers ? 'Hide members' : `Edit admins · ${members.length} members`}
        </button>
        {showMembers && members.map((uid) => (
          <MemberRow key={uid} chat={chat} uid={uid} myUid={myUid} amAdmin={amAdmin} busy={busy === uid}
            onToggleAdmin={(u) => run(u, () => toggleAdmin(chat, me, u))}
            onEditTag={(u, n, prof, t) => setTagging({ uid: u, name: n, profile: prof, tag: t })} />
        ))}
      </div>

      {/* 🏷 the tag editor — writes chats/{id}.participantTags, which is the same
          field the phone reads, so the change is on their handset at the next
          snapshot with nothing to relay */}
      {tagging && (
        <MemberTagEditor
          chat={chat}
          me={me}
          uid={tagging.uid}
          name={tagging.name}
          profile={tagging.profile}
          initialTag={tagging.tag}
          onClose={() => setTagging(null)}
        />
      )}

      {err && <div className="ci-note grp-err">{err}</div>}
      <div className="ci-note">
        Every change here posts the same system message your phone posts, so the group
        sees it in the chat and their phones are notified.
      </div>
    </div>
  );
}
